import { z } from 'zod';

import { financeAccess } from '../finance/finance-access';
import { PRESET_ROLES, type PresetRole } from '../roles';

import { LocalDateSchema } from './my-work.dto';
import { InsightKind } from './projects.dto';

/**
 * Team Overview (docs/release-1/17-V2-BUILD-PLAN.md §6 W5-0; concept §5.3).
 *
 *   GET /api/teams/:teamSlug/overview?today=YYYY-MM-DD   → TeamOverview
 *
 * Guards: JwtAuthGuard → TeamAccessGuard → PermissionGuard `team.view`.
 * Regular teams only (a Personal team has no Overview). `today` is the
 * viewer's local day (as on Home); without it the server uses "now" in the
 * viewer's time zone. Week maths follow the viewer's `weekStart`.
 *
 * PRIVACY (§2.2 P11, founder hard requirement): every number here is computed
 * from public task / milestone / time-entry facts with the shared metric
 * definitions (§2.3, `packages/shared/src/metrics`). Nothing — not a count, not
 * a boolean — is read from anyone's private plan, notes, steps or carry-over.
 * Workload = remaining ESTIMATES (`memberWorkload`), never planned time.
 *
 * Who sees what (§2.5; per-team role, permissions decide — see
 * `teamOverviewAccess`):
 *  - health / at-risk projects / milestones / insights: everyone except a
 *    Contractor (OUTSTAFF); Guest (VIEWER) read-only;
 *  - workload: every member's row with `time.view_team` (Lead+); only my own
 *    row with `time.view_own` (Member); `null` otherwise (Contractor, Guest);
 *  - billable: `time.view_billing` (Manager+), else `null`;
 *  - Contractor: `outstaffView = true` and NOTHING team-wide (enforced by the
 *    schema below) — the web shows "Your tasks in {team}" only.
 */

export const TeamOverviewQuerySchema = z.object({ today: LocalDateSchema.optional() }).strict();
export type TeamOverviewQuery = z.infer<typeof TeamOverviewQuerySchema>;

/** Health strip (Open tasks I can see in this team; §2.3 definitions, viewer's tz/week). */
export const TeamOverviewHealthSchema = z.object({
  /** `isOpen`. */
  open: z.number().int().min(0),
  /** `isOverdue`. */
  overdue: z.number().int().min(0),
  /** `isDueThisWeek` (Open only). */
  dueThisWeek: z.number().int().min(0),
  /** `isBlocked`. */
  blocked: z.number().int().min(0),
  /** `isDoneInRange` over [now − 7d, now). */
  doneLast7: z.number().int().min(0),
  /** `isDoneInRange` over [now − 14d, now − 7d). */
  donePrev7: z.number().int().min(0),
  /** `isStale` (Open ∧ not updated for 14 days). */
  stale: z.number().int().min(0),
  /** `isUnassignedOrNoDeadline` (Open ∧ (no assignee ∨ no Deadline)). */
  unassignedOrNoDeadline: z.number().int().min(0),
});
export type TeamOverviewHealth = z.infer<typeof TeamOverviewHealthSchema>;

/** All-zero health — what a Contractor (OUTSTAFF) view carries. */
export const EMPTY_TEAM_OVERVIEW_HEALTH: Readonly<TeamOverviewHealth> = Object.freeze({
  open: 0,
  overdue: 0,
  dueThisWeek: 0,
  blocked: 0,
  doneLast7: 0,
  donePrev7: 0,
  stale: 0,
  unassignedOrNoDeadline: 0,
});

/** Why a project is at risk (`isProjectAtRisk`): a milestone at risk/overdue, or Overdue ≥ 15 % of Open. */
export const TeamOverviewAtRiskReasonSchema = z.enum(['milestone', 'overdue_ratio']);
export type TeamOverviewAtRiskReason = z.infer<typeof TeamOverviewAtRiskReasonSchema>;

export const TeamOverviewAtRiskProjectSchema = z.object({
  projectId: z.string().uuid(),
  slug: z.string(),
  name: z.string(),
  /** 'milestone' wins when both apply. */
  reason: TeamOverviewAtRiskReasonSchema,
});
export type TeamOverviewAtRiskProject = z.infer<typeof TeamOverviewAtRiskProjectSchema>;

/**
 * One open milestone of a project I can see — for the at-risk list and the
 * timeline summary. The Gantt itself reuses `useTeamMilestones` +
 * `MilestonesGanttChart` (full `MilestoneProjection`s).
 */
export const TeamOverviewMilestoneSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  projectId: z.string().uuid(),
  projectSlug: z.string(),
  projectName: z.string(),
  plannedStartDate: LocalDateSchema.nullable(),
  plannedEndDate: LocalDateSchema.nullable(),
  progressPct: z.number().min(0).max(100),
  /** `isMilestoneAtRisk` (≤ 7 days to end ∧ progress < 50 %, or a linked task overdue). */
  atRisk: z.boolean(),
});
export type TeamOverviewMilestone = z.infer<typeof TeamOverviewMilestoneSchema>;

/** Most insights a response carries (server keeps risk → warning → info → positive). */
export const TEAM_OVERVIEW_MAX_INSIGHTS = 20;

/** One heuristic `ProjectInsightsService` bullet, rolled up per team (no LLM, free). */
export const TeamOverviewInsightSchema = z.object({
  kind: InsightKind,
  projectId: z.string().uuid(),
  projectSlug: z.string(),
  projectName: z.string(),
  message: z.string(),
});
export type TeamOverviewInsight = z.infer<typeof TeamOverviewInsightSchema>;

/** One member's load this week — from estimates and logged time only (§5.1). */
export const TeamOverviewWorkloadRowSchema = z.object({
  userId: z.string().uuid(),
  name: z.string(),
  avatarUrl: z.string().nullable(),
  /** `memberWorkload(...).remainingHours`: Σ max(0, estimate − spent), Open, Deadline ≤ end of week. */
  remainingHours: z.number().min(0),
  /** `memberWorkload(...).unestimatedCount`. */
  unestimatedCount: z.number().int().min(0),
  /** Hours logged in this team this calendar week (time entries). */
  hoursThisWeek: z.number().min(0),
  /** Hours logged in this team last calendar week. */
  hoursLastWeek: z.number().min(0),
});
export type TeamOverviewWorkloadRow = z.infer<typeof TeamOverviewWorkloadRowSchema>;

/** Billable value of time logged this calendar week (Manager+, `time.view_billing`). */
export const TeamOverviewBillableSchema = z.object({
  valueCents: z.number().int().min(0),
  /** ISO 4217, upper case (e.g. "USD"). */
  currency: z.string().regex(/^[A-Z]{3}$/),
});
export type TeamOverviewBillable = z.infer<typeof TeamOverviewBillableSchema>;

const TeamOverviewObjectSchema = z.object({
  team: z.object({ id: z.string().uuid(), slug: z.string(), name: z.string() }),
  /** My preset role in THIS team (a custom role reports the preset it derives from). */
  viewerRole: z.enum(PRESET_ROLES),
  health: TeamOverviewHealthSchema,
  /** Projects at risk, most overdue first. */
  atRiskProjects: z.array(TeamOverviewAtRiskProjectSchema),
  /** Open milestones, by planned end (undated last). */
  milestones: z.array(TeamOverviewMilestoneSchema),
  insights: z.array(TeamOverviewInsightSchema).max(TEAM_OVERVIEW_MAX_INSIGHTS),
  /** Lead+: every member; Member: only my row; Contractor / Guest: null. Most remaining first. */
  workload: z.array(TeamOverviewWorkloadRowSchema).nullable(),
  /** Manager+ only. */
  billable: TeamOverviewBillableSchema.nullable(),
  /** True for a Contractor: the web shows "Your tasks in {team}" and nothing team-wide. */
  outstaffView: z.boolean(),
});

/**
 * GET /api/teams/:teamSlug/overview. The refinement is a leak guard the API's
 * `.parse` enforces: a Contractor view can't carry any team-wide data.
 */
export const TeamOverviewSchema = TeamOverviewObjectSchema.superRefine((v, ctx) => {
  if (!v.outstaffView) return;
  const leaks: string[] = [];
  if (Object.values(v.health).some((n) => n !== 0)) leaks.push('health');
  if (v.atRiskProjects.length > 0) leaks.push('atRiskProjects');
  if (v.milestones.length > 0) leaks.push('milestones');
  if (v.insights.length > 0) leaks.push('insights');
  if (v.workload !== null) leaks.push('workload');
  if (v.billable !== null) leaks.push('billable');
  for (const path of leaks) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [path],
      message: 'A Contractor (outstaffView) overview carries no team-wide data',
    });
  }
});
export type TeamOverview = z.infer<typeof TeamOverviewSchema>;

/** What one viewer may see on Team Overview (§2.5). */
export interface TeamOverviewAccess {
  /** Contractor variant: "Your tasks in {team}" only. */
  outstaffView: boolean;
  /** 'all' = every member's row, 'own' = only mine, 'none' → `workload: null`. */
  workload: 'all' | 'own' | 'none';
  /** `billable` non-null. */
  billable: boolean;
}

/**
 * The §2.5 rule in one place for the API and tests. `rolePreset` is my preset
 * (or the preset my custom role derives from); `permissions` are my effective
 * scopes in this team.
 *
 * `billable` is `financeAccess(...).canView` (doc 18 §4.1, WS-H): a custom role
 * derived from VIEWER (or OUTSTAFF) never sees money, even with
 * `time.view_billing`. The API also nulls it for a PAT or an impersonation
 * (the channel rule) — that half is not a role fact, so it isn't here.
 */
export function teamOverviewAccess(
  rolePreset: PresetRole,
  permissions: Iterable<string>,
): TeamOverviewAccess {
  if (rolePreset === 'OUTSTAFF') return { outstaffView: true, workload: 'none', billable: false };
  const has = new Set(permissions);
  const workload = has.has('time.view_team') ? 'all' : has.has('time.view_own') ? 'own' : 'none';
  return { outstaffView: false, workload, billable: financeAccess(rolePreset, has).canView };
}

/**
 * Team Overview › a health tile's tasks (mobile tiles are tappable):
 *
 *   GET /api/teams/:teamSlug/overview/tasks?metric=overdue  → TeamOverviewTasks
 *
 * The SAME shared metric fragment that produced the tile's number selects the
 * rows, so "Overdue 5" opens exactly those 5 (up to `TEAM_OVERVIEW_TASKS_LIMIT`;
 * `total` is the full count). Same guards, RLS and privacy rule as the overview
 * (public task facts only); a Contractor gets 403 (nothing team-wide).
 */
export const TEAM_OVERVIEW_TASK_METRICS = [
  'open',
  'overdue',
  'due_this_week',
  'blocked',
  'done_7d',
  'stale',
  'unassigned_or_no_deadline',
] as const;
export type TeamOverviewTaskMetric = (typeof TEAM_OVERVIEW_TASK_METRICS)[number];
export const TEAM_OVERVIEW_TASKS_LIMIT = 100;

export const TeamOverviewTasksQuerySchema = z
  .object({ metric: z.enum(TEAM_OVERVIEW_TASK_METRICS), today: LocalDateSchema.optional() })
  .strict();
export type TeamOverviewTasksQuery = z.infer<typeof TeamOverviewTasksQuerySchema>;

export const TeamOverviewTaskItemSchema = z.object({
  id: z.string().uuid(),
  /** "WEB-12". */
  key: z.string(),
  title: z.string(),
  projectSlug: z.string(),
  projectName: z.string(),
  statusName: z.string().nullable(),
  statusCategory: z.enum(['open', 'in_progress', 'review', 'done', 'cancelled']),
  dueAt: z.string().datetime({ offset: true }).nullable(),
  assigneeName: z.string().nullable(),
});
export type TeamOverviewTaskItem = z.infer<typeof TeamOverviewTaskItemSchema>;

export const TeamOverviewTasksSchema = z.object({
  metric: z.enum(TEAM_OVERVIEW_TASK_METRICS),
  items: z.array(TeamOverviewTaskItemSchema).max(TEAM_OVERVIEW_TASKS_LIMIT),
  total: z.number().int().min(0),
});
export type TeamOverviewTasks = z.infer<typeof TeamOverviewTasksSchema>;
